import { t, type StaffLanguage } from './staffText'
import type { VisitField } from './types'

/**
 * What a ticket may carry beyond its number, as the reception desk edits it.
 *
 * Every rule the editor turns on lives here rather than in the screen, because
 * the web suite has no DOM and a rule inside a component is a rule nothing runs
 * until a branch runs it. There are five of them and each one is the kind of
 * mistake that costs a customer rather than a build:
 *
 * who may be offered the editor at all, which declarations it offers, whether a
 * typed value would be refused before it is sent, what a save actually sends,
 * and which sentence of a refusal belongs beside which box.
 *
 * The one thing to hold in mind reading this file: a save sends the whole map
 * and names the keys it changed (`changedKeys`); the server writes only those,
 * and a changed key missing from the map is cleared.
 */

/** The permission that lists the declarations to the floor.
 *
 *  Spelled out here rather than in the screen so a test can run it. `GET /fields`
 *  answers two readers: with `?include=retired` it is the administration read and
 *  still needs `catalogue.manage`, and plain it is the floor read — what a ticket
 *  may carry right now — registered under `ticket.read`. This is the plain one,
 *  because this editor is the floor. */
export const DECLARATIONS = 'ticket.read'

/** The server's own ceiling on any value, whatever a declaration asks for.
 *  catalog.MaxFieldValue. */
export const MAX_FIELD_VALUE = 400

/**
 * Whether this person can be offered the editor.
 *
 * This used to be a narrower question than "may this person set a value", and
 * the gap was the reason the editor existed for almost nobody: `PATCH
 * /tickets/{id}/fields` accepts anybody who can issue a ticket at the branch —
 * reception included, deliberately — while the only route that said what the
 * declarations were needed catalogue.manage, which reception and supervisor do
 * not hold. An editor that cannot read the declarations cannot honour a field's
 * type, offer a choice field's choices or mark a personal one, so it would have
 * been a row of text boxes that guesses, which is worse than no editor.
 *
 * The route was split instead, so the two questions now have the same answer for
 * the people who matter: the floor reads the declarations it is expected to fill
 * in. The check stays because a screen should still not invite somebody into a
 * panel that will refuse them.
 */
export function readsDeclarations(permissions: readonly string[]): boolean {
  return permissions.includes(DECLARATIONS)
}

/**
 * The declarations the editor offers, in the order it draws them.
 *
 * Retired ones are left out: the server refuses a retired key outright — "was
 * retired, so nothing new may be stored in it" — so a box for one is a box that
 * cannot be saved. `GET /fields` lists them because the administration screen is
 * how a withdrawn declaration is found again; this is not that screen.
 *
 * Sorted by the organisation's own order, then by key so two declarations given
 * the same order do not swap places between loads.
 */
export function offeredFields(declared: readonly VisitField[]): VisitField[] {
  return declared
    .filter((f) => f.active)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key))
}

/**
 * Keys this ticket is carrying that no box can hold: undeclared, or retired.
 *
 * These matter because a save replaces the map. Sending them back is refused —
 * that is what retirement means — and leaving them out drops them. Either way
 * the value goes, so the panel says so before anybody presses Save rather than
 * after, which is the only honest thing it can do.
 */
export function strandedKeys(
  declared: readonly VisitField[],
  held: Readonly<Record<string, string>> | undefined,
): string[] {
  if (!held) return []
  const offered = new Set(offeredFields(declared).map((f) => f.key))
  return Object.keys(held)
    .filter((k) => !offered.has(k))
    .sort()
}

/** What this field lets a value be, in characters. Zero, or anything past the
 *  product's own ceiling, means the ceiling. catalog.Field.CheckValue. */
export function limitOf(field: Pick<VisitField, 'maxLen'>): number {
  const declared = field.maxLen ?? 0
  return declared <= 0 || declared > MAX_FIELD_VALUE ? MAX_FIELD_VALUE : declared
}

/**
 * A phone number written as E.164, or null — catalog.NormalisePhone, rule for rule.
 * Spaces, dashes, dots and brackets go; a leading 00 is the plus; no country is
 * guessed. The server stores what this returns, so reception can show the operator
 * the number exactly as the customer will be texted at it.
 */
export function normalisePhone(value: string): string | null {
  const trimmed = value.trim()
  if (!/^\+?[0-9 ().-]*$/.test(trimmed)) return null
  let digits = trimmed.replace(/[ ().-]/g, '')
  if (digits.startsWith('00')) digits = '+' + digits.slice(2)
  return /^\+[1-9][0-9]{7,14}$/.test(digits) ? digits : null
}

/**
 * Why this value would be refused, or an empty string if it would not.
 *
 * The sentences are the server's own, word for word, from catalog.Field.
 * CheckValue — so the refusal an operator reads before pressing Save is the
 * refusal they would have read after, rather than a second vocabulary for the
 * same five rules.
 *
 * An empty value is never a problem. Empty means the field is not being set, and
 * what a save does with it is `fieldsToSend`'s business, not this one's.
 *
 * Deliberately no stricter than the server anywhere. A check that refuses
 * something the server would have taken is a value an operator cannot enter and
 * no support call can explain, so where the two could disagree — Go parses a few
 * number spellings a browser does not — this side lets it through and lets the
 * server have the last word. The other direction only costs a round trip.
 */
export function valueProblem(field: VisitField, value: string, lang: StaffLanguage): string {
  if (value === '') return ''

  const limit = limitOf(field)
  const length = [...value].length
  if (length > limit) {
    return t('reception.field.tooLong', lang, { key: field.key, limit, length })
  }

  switch (field.type) {
    case 'number':
      if (!Number.isFinite(Number(value))) {
        return t('reception.field.notNumber', lang, { key: field.key, value })
      }
      return ''
    case 'date':
      if (!isCalendarDate(value)) {
        return t('reception.field.notDate', lang, { key: field.key, value })
      }
      return ''
    case 'boolean':
      if (value !== 'true' && value !== 'false') {
        return t('reception.field.notBoolean', lang, { key: field.key, value })
      }
      return ''
    case 'phone':
      if (normalisePhone(value) === null) {
        return t('reception.field.notPhone', lang, { key: field.key, value })
      }
      return ''
    case 'choice':
      if (!(field.choices ?? []).includes(value)) {
        return t('reception.field.notChoice', lang, { key: field.key, value, choices: (field.choices ?? []).join(', ') })
      }
      return ''
    default:
      return ''
  }
}

/** A real day written the one way the server reads it. "2026-13-01" parses as a
 *  Date in a browser and does not in Go, so the month and day are checked back
 *  against what was typed rather than trusted to the parse. */
function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const parts = value.split('-').map(Number)
  const year = parts[0] as number
  const month = parts[1] as number
  const day = parts[2] as number
  const at = new Date(Date.UTC(year, month - 1, day))
  return (
    at.getUTCFullYear() === year && at.getUTCMonth() === month - 1 && at.getUTCDate() === day
  )
}

/**
 * The map a save sends.
 *
 * Everything the editor holds a value for, and nothing else — which is how a
 * clear happens, because the route replaces rather than merges and an omitted
 * key is a key that goes. A box emptied on screen is therefore a value deleted
 * on the ticket, and that is the whole of the clearing mechanism.
 *
 * Values are trimmed, and a value that was only spaces counts as emptied. A name
 * with a trailing space is a name somebody typed carelessly, not a different
 * name, and a ticket carrying " " under `visitor.name` is a ticket that looks
 * set and reads blank everywhere it is printed.
 *
 * Only offered fields are read. A draft left behind by a declaration that was
 * retired while the screen was open is not sent, because sending it is refused.
 */
export function fieldsToSend(
  offered: readonly VisitField[],
  drafts: Readonly<Record<string, string>>,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const field of offered) {
    const value = (drafts[field.key] ?? '').trim()
    if (value !== '') out[field.key] = value
  }
  return out
}

/** Whether a save would change anything on this ticket. Save is not offered for
 *  a map identical to the one the ticket already carries: the request would
 *  succeed, write an audit row and mean nothing. */
export function changed(
  held: Readonly<Record<string, string>> | undefined,
  sending: Readonly<Record<string, string>>,
): boolean {
  const before = held ?? {}
  const keys = new Set([...Object.keys(before), ...Object.keys(sending)])
  for (const key of keys) {
    if (before[key] !== sending[key]) return true
  }
  return false
}

/** The keys a save writes: those whose value differs from what the drawer
 *  opened with. The server merges them onto what is stored now, so a value the
 *  customer withdrew meanwhile is not written back. */
export function changedKeys(
  held: Readonly<Record<string, string>> | undefined,
  sending: Readonly<Record<string, string>>,
): string[] {
  const before = held ?? {}
  const keys = new Set([...Object.keys(before), ...Object.keys(sending)])
  return [...keys].filter((key) => before[key] !== sending[key]).sort()
}

/**
 * What a save sends, judged against the values the drawer opened with rather
 * than the live ticket: a refresh after the customer withdrew their number must
 * not make the untouched box look changed and write the number back. Keys the
 * ticket carried at opening that no box holds are cleared, as `strandedNote` says.
 */
export function fieldsSave(
  offered: readonly VisitField[],
  opened: Readonly<Record<string, string>> | undefined,
  drafts: Readonly<Record<string, string>>,
): { fields: Record<string, string>; changed: string[] } {
  const fields = fieldsToSend(offered, drafts)
  const before = fieldsToSend(offered, draftsFrom(offered, opened))
  const boxed = new Set(offered.map((f) => f.key))
  const stranded = Object.keys(opened ?? {}).filter((k) => !boxed.has(k))
  return { fields, changed: [...new Set([...changedKeys(before, fields), ...stranded])].sort() }
}

/** What the boxes start out holding: whatever the ticket carries today, for the
 *  fields that have a box. Values under keys with no box are not carried — see
 *  `strandedKeys`, which is what says so on screen. */
export function draftsFrom(
  offered: readonly VisitField[],
  held: Readonly<Record<string, string>> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const field of offered) {
    out[field.key] = held?.[field.key] ?? ''
  }
  return out
}

/** A refusal, split up so each sentence sits beside the box it is about. */
export interface Refusal {
  /** One sentence per field key the server complained about. */
  byKey: Record<string, string>
  /** Whatever named no field, kept rather than dropped: a refusal an operator
   *  cannot see is a save that failed silently. */
  rest: string
}

/**
 * Sort a refusal into the boxes that caused it.
 *
 * `checkFields` reports every fault rather than the first and joins them with
 * "; ", and each sentence it writes begins with the key it is about — quoted for
 * the two declaration faults, bare for the five value faults. So the message can
 * be taken apart again and shown where it is useful, which is next to the box
 * somebody typed into and not in the red line at the top of the other column.
 *
 * Anything that names no key at all — a refusal from somewhere else entirely, a
 * sentence a later server writes differently — falls into `rest` and is shown
 * whole. Losing it would be worse than showing it in the wrong place.
 */
export function refusalFor(message: string, keys: readonly string[]): Refusal {
  const byKey: Record<string, string> = {}
  const rest: string[] = []
  for (const part of message.split('; ')) {
    const sentence = part.trim()
    if (sentence === '') continue
    const key = keyNamedBy(sentence, keys)
    if (key === '') rest.push(sentence)
    else byKey[key] = byKey[key] ? `${byKey[key]}; ${sentence}` : sentence
  }
  return { byKey, rest: rest.join('; ') }
}

/** The key a refusal is about, or an empty string. The longest match wins, so
 *  that a message about `visitor.name` is not filed under `visitor` on an
 *  organisation that declares both. */
function keyNamedBy(sentence: string, keys: readonly string[]): string {
  let found = ''
  for (const key of keys) {
    if (key.length <= found.length) continue
    if (sentence.startsWith(`"${key}"`) || sentence.startsWith(`${key} `)) found = key
  }
  return found
}

/** What the marker beside a personal field says when somebody stops on it.
 *
 *  Plain, and about the value rather than about the declaration: the person
 *  typing is a clerk with a customer in front of them, not the administrator who
 *  ticked the box. */
export function personalNote(lang: StaffLanguage): string {
  return t('reception.personalNote', lang)
}

/** What the panel says about a ticket whose values no box can hold. One
 *  sentence, naming them, because the next save takes them away. */
export function strandedNote(keys: readonly string[], lang: StaffLanguage): string {
  if (keys.length === 0) return ''
  return t('reception.stranded', lang, { list: keys.join(', '), n: keys.length })
}
